import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoText from '../../../components/atoms/InfoText';
import Toggle from '../../../components/atoms/Toggle';
import FormField from '../../../components/FormField/FormField';

interface PlanPricingSectionProps {
  allowExtraProducts: boolean;
  onToggleAllowExtraProducts: (checked: boolean) => void;
}

export default function PlanPricingSection({
  allowExtraProducts,
  onToggleAllowExtraProducts,
}: PlanPricingSectionProps) {
  const { t } = useTranslation();

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="section-plan-price"
    >
      <div className="flex flex-col gap-1.5" data-testid="plan-price-header">
        <InfoText
          text={t('home.plan-price-title')}
          className="text-[20px] !font-bold !text-light-text-primary dark:!text-dark-text-primary !leading-[100%]"
        />
        <InfoText
          text={t('home.plan-price-desc')}
          className="!text-light-text-secondary dark:!text-dark-text-secondary text-[16px] !font-normal leading-normal"
        />
      </div>

      {/* Monthly (SAR) and Coins */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div data-testid="field-monthly-sar-container">
          <FormField
            type="number"
            id="monthlySar"
            name="monthlySar"
            label={t('home.monthly-sar')}
            labelClassName="!text-[16px] !font-semibold !text-light-text-primary dark:!text-dark-text-primary mb-2"
            placeholder={t('home.set')}
            required
            min={0}
            data-testid="field-monthly-sar"
          />
        </div>
        <div data-testid="field-monthly-coins-container">
          <FormField
            type="number"
            id="monthlyCoins"
            name="monthlyCoins"
            label={t('home.coins')}
            labelClassName="!text-[16px] !font-semibold !text-light-text-primary dark:!text-dark-text-primary mb-2"
            placeholder={t('home.set')}
            required
            min={0}
            data-testid="field-monthly-coins"
          />
        </div>
      </div>

      {/* Yearly (SAR) and Coins */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div data-testid="field-yearly-sar-container">
          <FormField
            type="number"
            id="yearlySar"
            name="yearlySar"
            label={t('home.yearly-sar')}
            labelClassName="!text-[16px] !font-semibold !text-light-text-primary dark:!text-dark-text-primary mb-2"
            placeholder={t('home.set')}
            required
            min={0}
            data-testid="field-yearly-sar"
          />
        </div>
        <div data-testid="field-yearly-coins-container">
          <FormField
            type="number"
            id="yearlyCoins"
            name="yearlyCoins"
            label={t('home.coins')}
            labelClassName="!text-[16px] !font-semibold !text-light-text-primary dark:!text-dark-text-primary mb-2"
            placeholder={t('home.set')}
            required
            min={0}
            data-testid="field-yearly-coins"
          />
        </div>
      </div>

      {/* Allow extra products Toggle row */}
      <div
        className="flex items-center justify-between pt-1"
        data-testid="allow-extra-products-row"
      >
        <div className="flex flex-col gap-1" data-testid="allow-extra-products-text">
          <InfoText
            text={t('home.allow-extra-products')}
            className="text-[16px] !font-bold !text-light-text-primary dark:!text-dark-text-primary !leading-tight"
          />
          <InfoText
            text={t('home.allow-extra-products-desc')}
            className="text-[14px] !font-normal !text-light-text-secondary dark:!text-dark-text-secondary !leading-tight"
          />
        </div>

        <Toggle
          checked={allowExtraProducts}
          onChange={onToggleAllowExtraProducts}
          data-testid="allow-extra-products-toggle"
        />
      </div>

      {/* Extra fees inputs (visible when toggle enabled) */}
      {allowExtraProducts && (
        <div
          className="flex flex-col gap-6"
          data-testid="extra-fees-container"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div data-testid="field-extra-fee-monthly-coins-container">
              <FormField
                type="number"
                id="extraFeeMonthlyCoins"
                name="extraFeeMonthlyCoins"
                label={t('home.extra-fee-monthly-coins')}
                labelClassName="!text-[16px] !font-semibold !text-light-text-primary dark:!text-dark-text-primary mb-2"
                placeholder={t('home.set')}
                required
                min={0}
                data-testid="field-extra-fee-monthly-coins"
              />
            </div>
            <div data-testid="field-extra-fee-monthly-sar-container">
              <FormField
                type="number"
                id="extraFeeMonthlySar"
                name="extraFeeMonthlySar"
                label={t('home.extra-fee-monthly-sar')}
                labelClassName="!text-[16px] !font-semibold !text-light-text-primary dark:!text-dark-text-primary mb-2"
                placeholder={t('home.set')}
                required
                min={0}
                data-testid="field-extra-fee-monthly-sar"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div data-testid="field-extra-fee-yearly-coins-container">
              <FormField
                type="number"
                id="extraFeeYearlyCoins"
                name="extraFeeYearlyCoins"
                label={t('home.extra-fee-yearly-coins')}
                labelClassName="!text-[16px] !font-semibold !text-light-text-primary dark:!text-dark-text-primary mb-2"
                placeholder={t('home.set')}
                required
                min={0}
                data-testid="field-extra-fee-yearly-coins"
              />
            </div>
            <div data-testid="field-extra-fee-yearly-sar-container">
              <FormField
                type="number"
                id="extraFeeYearlySar"
                name="extraFeeYearlySar"
                label={t('home.extra-fee-yearly-sar')}
                labelClassName="!text-[16px] !font-semibold !text-light-text-primary dark:!text-dark-text-primary mb-2"
                placeholder={t('home.set')}
                required
                min={0}
                data-testid="field-extra-fee-yearly-sar"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
